/**
 * Simulation setup and multi-strategy runs (SPEC-V2.md §3.3, §5, §6.1).
 *
 * Everything variable lives here rather than on a strategy. A strategy is an
 * arrangement of bets; a buy-in, a walk-away marker and a chip value are
 * properties of a *test* of one. Keeping them apart is what makes two
 * strategies comparable at all — put the stopping rules on the strategy and you
 * can no longer tell whether one beat the other because of its layout or
 * because it happened to carry a tighter stop.
 *
 * The chip amount is the one deliberate exception: it is set per strategy,
 * because stake size is itself a thing worth testing. The cost of that freedom
 * is that an entry differing in both layout and stake cannot be attributed to
 * either, which the app discloses rather than resolves — see §9.2, and the
 * `denominationCents` carried on every result so no comparison can omit it.
 *
 * Pure and framework-free; the worker imports it directly.
 */

import type { PlacedBet, TableProfile } from './types';
import type { CappedBet } from './limits';
import type { SessionEnd, SessionResult, SessionRules } from './session';
import type { LayeredPlacement, SessionTrace } from './layered';
import { placeLayers, playLayeredSession, traceSession } from './layered';
import { runCampaignWith } from './session';
import { normaliseBets, analyseSpread } from './spread';
import { spreadVariance } from './simulate';
import { createRng } from './rng';

export interface StrategyEntry {
  readonly strategyId: string;
  /** What one chip is worth for this strategy, in integer cents. */
  readonly denominationCents: number;
}

export interface SimulationSetup {
  readonly entries: readonly StrategyEntry[];
  /** Buy-in, pocket threshold and both stops — shared by every entry. */
  readonly rules: SessionRules;
  readonly sessionsPerCampaign: number;
  readonly campaigns: number;
  readonly seed: number | string;
}

/**
 * The table as it behaves for one strategy's chip value.
 *
 * A spread holds abstract chips; the entry says what a chip is worth. Since
 * `baseUnit` is precisely "what one chip is worth at this table", the
 * denomination *is* the base unit for that run, and every downstream figure —
 * stakes, limits in cents, capped chip counts — follows without special cases.
 * Payouts and the zero rule are untouched, so nothing about the table's odds is
 * being rewritten (invariant 6).
 */
export function tableAtDenomination(
  table: TableProfile,
  denominationCents: number,
): TableProfile {
  return { ...table, baseUnit: denominationCents };
}

export interface Distribution {
  readonly p10: number;
  readonly median: number;
  readonly p90: number;
}

export interface StrategyStats {
  readonly strategyId: string;
  readonly denominationCents: number;
  readonly seed: string;
  readonly campaigns: number;
  readonly sessionsPerCampaign: number;

  /** Stake put up each spin, after limits, in cents. */
  readonly stakeCentsPerSpin: number;
  readonly pocketsCovered: number;

  /** Individual sessions that came home above the buy-in. */
  readonly sessionAheadProbability: number;
  /** Whole campaigns that finished with the bank ahead of real money in. */
  readonly campaignAheadProbability: number;

  readonly campaignNetCents: Distribution;
  readonly sessionPocketedCents: Distribution;
  readonly peakOutOfPocketCents: Distribution;
  readonly worstPeakOutOfPocketCents: number;
  readonly spinsToGate: Distribution;

  readonly endCounts: Readonly<Record<SessionEnd, number>>;
  readonly endShares: Readonly<Record<SessionEnd, number>>;

  readonly totalStakedCents: number;
  readonly totalSpins: number;
  readonly realisedEdge: number;
  readonly theoreticalEdge: number;
  /**
   * Lower bound on the uncertainty in `realisedEdge`. It is
   * `sqrt(variance / spins)`, which treats the spin count as fixed when a
   * session's length is decided by its stops — measured about 25% narrower than
   * the spread actually observed between seeds. Reported as a floor, never as a
   * confident interval (§6.1).
   */
  readonly edgeStandardError: number;

  /** Limit breaches, named so §4.4 can report them per entry. */
  readonly capped: readonly CappedBet[];
  readonly unplayable: boolean;
}

export interface StrategyRun {
  readonly stats: StrategyStats;
  /** One labelled session from the distribution — never the headline (§6.2). */
  readonly trace: SessionTrace;
  readonly placement: LayeredPlacement;
}

export interface RunHooks {
  readonly onProgress?: (progress: { completed: number; total: number }) => void;
  readonly shouldCancel?: () => boolean;
}

/**
 * Seed for one session, so the same session index sees the same spins for every
 * strategy in a run.
 *
 * Sharing a single stream across a campaign would only hold the comparison
 * together until the first strategy ended a session on a different spin;
 * everything after that would be a different wheel. Deriving per session keeps
 * strategy A's third session and strategy B's third session identical however
 * long their first two ran, which is the unit a walk-away marker applies to.
 *
 * Exported because that guarantee is the contract, not an implementation
 * detail: a caller reproducing a session, or a test pinning the property, has
 * to derive the same seed, and doing it by copying the format string would let
 * the two drift apart without anything failing loudly.
 */
export function sessionSeed(
  seed: number | string,
  campaign: number,
  session: number,
): string {
  return `${seed}|c${campaign}|s${session}`;
}

export function runStrategyBatch(
  input: {
    readonly strategyId: string;
    readonly layerBets: readonly (readonly PlacedBet[])[];
    readonly table: TableProfile;
    readonly denominationCents: number;
    readonly rules: SessionRules;
    readonly sessionsPerCampaign: number;
    readonly campaigns: number;
    readonly seed: number | string;
  },
  hooks: RunHooks = {},
): StrategyRun | null {
  const table = tableAtDenomination(input.table, input.denominationCents);
  const placement = placeLayers(input.layerBets, table);
  const merged = normaliseBets(input.layerBets.flatMap((bets) => [...bets]));
  const analysis = analyseSpread(merged, table.wheelStyle, table);

  const campaigns = Math.max(1, input.campaigns);
  const sessions = Math.max(1, input.sessionsPerCampaign);

  const campaignNets: number[] = [];
  const peaks: number[] = [];
  const pocketed: number[] = [];
  const spinsToGate: number[] = [];
  const sessionNets: number[] = [];
  const endCounts: Record<SessionEnd, number> = {
    bust: 0,
    'stop-loss': 0,
    spins: 0,
    'pocket-target': 0,
    unplayable: 0,
    'max-spins': 0,
  };

  let totalStaked = 0;
  let totalSpins = 0;
  let campaignsAhead = 0;
  let sessionsAhead = 0;
  let sessionCount = 0;

  const reportEvery = Math.max(1, Math.floor(campaigns / 100));

  for (let campaign = 0; campaign < campaigns; campaign += 1) {
    if (hooks.shouldCancel?.()) return null;

    const result = runCampaignWith(
      (_rng, sessionIndex) => {
        const played: SessionResult = playLayeredSession(
          placement,
          table,
          input.rules,
          createRng(sessionSeed(input.seed, campaign, sessionIndex)),
        );
        pocketed.push(played.pocketedCents);
        spinsToGate.push(played.spins);
        sessionCount += 1;
        if (played.netCents > 0) sessionsAhead += 1;
        return played;
      },
      input.rules,
      sessions,
      createRng(sessionSeed(input.seed, campaign, -1)),
    );

    campaignNets.push(result.netCents);
    peaks.push(result.peakOutOfPocketCents);
    sessionNets.push(...result.sessionNets);
    totalStaked += result.totalStakedCents;
    totalSpins += result.totalSpins;
    if (result.netCents > 0) campaignsAhead += 1;
    for (const end of Object.keys(endCounts) as SessionEnd[]) {
      endCounts[end] += result.endCounts[end];
    }

    if (campaign % reportEvery === 0) {
      hooks.onProgress?.({ completed: campaign, total: campaigns });
    }
  }
  hooks.onProgress?.({ completed: campaigns, total: campaigns });

  // The ledger is the very first session of the first campaign, not a separate
  // draw — so the story it tells is genuinely one of the samples behind the
  // distribution rather than a run that happened alongside it.
  const trace = traceSession(
    input.layerBets,
    table,
    input.rules,
    sessionSeed(input.seed, 0, 0),
  );

  const totalNet = campaignNets.reduce((sum, value) => sum + value, 0);
  const variance = spreadVariance({ bets: merged, wheelStyle: table.wheelStyle, table });
  const endShares = {} as Record<SessionEnd, number>;
  for (const end of Object.keys(endCounts) as SessionEnd[]) {
    endShares[end] = sessionCount > 0 ? endCounts[end] / sessionCount : 0;
  }

  return {
    placement,
    trace,
    stats: {
      strategyId: input.strategyId,
      denominationCents: input.denominationCents,
      seed: String(input.seed),
      campaigns,
      sessionsPerCampaign: sessions,
      stakeCentsPerSpin: placement.stakeCents,
      pocketsCovered: analysis.pocketsCovered,
      sessionAheadProbability: sessionCount > 0 ? sessionsAhead / sessionCount : 0,
      campaignAheadProbability: campaignsAhead / campaigns,
      campaignNetCents: distributionOf(campaignNets),
      sessionPocketedCents: distributionOf(pocketed),
      peakOutOfPocketCents: distributionOf(peaks),
      worstPeakOutOfPocketCents: peaks.reduce((worst, value) => Math.max(worst, value), 0),
      spinsToGate: distributionOf(spinsToGate),
      endCounts,
      endShares,
      totalStakedCents: totalStaked,
      totalSpins,
      realisedEdge: totalStaked > 0 ? totalNet / totalStaked : 0,
      theoreticalEdge: analysis.expectedValuePct,
      edgeStandardError: totalSpins > 0 ? Math.sqrt(variance / totalSpins) : 0,
      capped: placement.capped,
      unplayable: placement.unplayable,
    },
  };
}

/**
 * Run every entry in a setup, each on the same seed.
 *
 * Returns `null` if cancelled. Entries are run in order and reported in order,
 * so the caller's list and the results line up without a lookup.
 */
export function runSetup(
  setup: SimulationSetup,
  resolve: (strategyId: string) => {
    readonly layerBets: readonly (readonly PlacedBet[])[];
    readonly table: TableProfile;
  } | null,
  hooks: RunHooks = {},
): StrategyRun[] | null {
  const runs: StrategyRun[] = [];

  for (let index = 0; index < setup.entries.length; index += 1) {
    const entry = setup.entries[index]!;
    const resolved = resolve(entry.strategyId);
    if (!resolved) continue;

    const run = runStrategyBatch(
      {
        strategyId: entry.strategyId,
        layerBets: resolved.layerBets,
        table: resolved.table,
        denominationCents: entry.denominationCents,
        rules: setup.rules,
        sessionsPerCampaign: setup.sessionsPerCampaign,
        campaigns: setup.campaigns,
        seed: setup.seed,
      },
      {
        shouldCancel: hooks.shouldCancel,
        onProgress: (progress) =>
          hooks.onProgress?.({
            completed: index * setup.campaigns + progress.completed,
            total: setup.entries.length * setup.campaigns,
          }),
      },
    );
    if (!run) return null;
    runs.push(run);
  }

  return runs;
}

// ---------------------------------------------------------------------------
// Statistics
// ---------------------------------------------------------------------------

export function percentile(sorted: readonly number[], fraction: number): number {
  if (sorted.length === 0) return 0;
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.round(fraction * (sorted.length - 1))),
  );
  return sorted[index] ?? 0;
}

function distributionOf(values: readonly number[]): Distribution {
  const sorted = [...values].sort((a, b) => a - b);
  return {
    p10: percentile(sorted, 0.1),
    median: percentile(sorted, 0.5),
    p90: percentile(sorted, 0.9),
  };
}

// ---------------------------------------------------------------------------
// The variance statement (SPEC.md §9, SPEC-V2.md §9.5)
// ---------------------------------------------------------------------------

export interface EdgeVerdict {
  /** How many standard errors the realised edge sits from the theoretical one. */
  readonly sigma: number;
  readonly withinNormal: boolean;
  readonly statement: string;
}

/**
 * Whether a run's realised edge is ordinary for its sample size, in words.
 *
 * §9 requires a plain statement after a simulation, because a realised −2.4%
 * against a theoretical −2.7% invites exactly one wrong conclusion, and the
 * only defence is saying out loud how much a run of that length wanders.
 *
 * The threshold is 2.5 rather than the textbook 2, and the wording refuses to
 * quote the multiple as a confident interval, because `edgeStandardError` is a
 * floor rather than the real figure: it is `sqrt(variance / spins)`, which
 * treats the spin count as fixed when a session's length is decided by its
 * stops. Measured across five seeds the spread between runs came out about 25%
 * wider than the formula predicts, so a result at "two standard errors" is
 * inside ordinary variance more often than that number suggests.
 */
export function edgeVerdict(stats: StrategyStats): EdgeVerdict {
  const observed = `${(stats.realisedEdge * 100).toFixed(2)}%`;
  const expected = `${(stats.theoreticalEdge * 100).toFixed(2)}%`;
  const spins = stats.totalSpins.toLocaleString();

  if (stats.totalSpins === 0 || stats.edgeStandardError <= 0) {
    return { sigma: 0, withinNormal: true, statement: 'No spins were played.' };
  }

  const sigma = (stats.realisedEdge - stats.theoreticalEdge) / stats.edgeStandardError;
  const magnitude = Math.abs(sigma);
  const withinNormal = magnitude <= 2.5;
  const tail = withinNormal
    ? 'ordinary variance for this sample size.'
    : 'unusual for this sample size, though a run of any length still says nothing about the next spin.';

  return {
    sigma,
    withinNormal,
    statement:
      `Observed ${observed} against a theoretical ${expected} over ${spins} spins — ` +
      `about ${magnitude.toFixed(1)} standard errors, which is ${tail} ` +
      'That count is a lower bound on the real uncertainty: it assumes a fixed number of ' +
      'spins, and a session that ends on a stop does not have one.',
  };
}
